/**
 * LongPostMetadataEditor Component
 * 
 * Comprehensive form component for creating and editing long post metadata including:
 * - Basic information (title, description, language, author)
 * - Source information (source title, URL, thumbnail)
 * - Content categorization (category, tags)
 * - Publishing status management
 * - Form validation and error handling
 * - Real-time thumbnail preview
 * - Dynamic tag management
 * 
 * Used in: LongPostList and LongPostManager for metadata editing
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Save, Cancel, Warning } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import LevelSelector from '../shared/LevelSelector';

const LongPostMetadataEditor = ({ post, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        language: '',
        level: '',
        author: '',
        sourceTitle: '',
        sourceUrl: '',
        thumbUrl: '',
        category: 'other',
        tags: [],
        isReadyToServe: false
    });

    const [languages, setLanguages] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [readyToServeDialog, setReadyToServeDialog] = useState({ open: false });

    const categories = [
        { value: 'news', label: 'News' },
        { value: 'biography', label: 'Biography' },
        { value: 'tutorial', label: 'Tutorial' },
        { value: 'science', label: 'Science' },
        { value: 'academic', label: 'Academic' },
        { value: 'book', label: 'Book' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        fetchLanguages();
        if (post) {
            setFormData({
                title: post.title || '',
                summary: post.summary || '',
                language: post.language?._id || post.language || '',
                level: post.level?._id || post.level || '',
                author: post.author || '',
                sourceTitle: post.sourceTitle || '',
                sourceUrl: post.sourceUrl || '',
                thumbUrl: post.thumbUrl || '',
                category: post.category || 'other',
                tags: post.tags || [],
                isReadyToServe: post.isReadyToServe || false
            });
        }
    }, [post]);

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            setLanguages(response.data);
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()]
            }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleTagInputKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.language) {
            newErrors.language = 'Language is required';
        }

        if (!formData.level) {
            newErrors.level = 'Learner level is required';
        }

        if (formData.tags.length === 0) {
            newErrors.tags = 'At least one tag is required';
        }

        if (formData.sourceUrl && !isValidUrl(formData.sourceUrl)) {
            newErrors.sourceUrl = 'Please enter a valid URL';
        }

        if (formData.thumbUrl && !isValidUrl(formData.thumbUrl)) {
            newErrors.thumbUrl = 'Please enter a valid URL';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            await onSave(formData);
        } catch (error) {
            console.error('Error saving long post:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReadyToServeToggle = () => {
        if (!formData.isReadyToServe) {
            // User is trying to set to ready - show warning dialog
            setReadyToServeDialog({ open: true });
        } else {
            // User is turning it off - allow immediately
            setFormData(prev => ({
                ...prev,
                isReadyToServe: false
            }));
        }
    };

    const handleReadyToServeConfirm = (confirmed) => {
        setReadyToServeDialog({ open: false });
        if (confirmed) {
            setFormData(prev => ({
                ...prev,
                isReadyToServe: true
            }));
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {post ? 'Edit Post Metadata' : 'Create Long Post'}
                </Typography>
                <Box>
                    <Button 
                        onClick={onCancel} 
                        sx={{ mr: 2 }}
                        startIcon={<Cancel />}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={<Save />}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Title"
                                value={formData.title}
                                onChange={handleInputChange('title')}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                select
                                label="Language"
                                value={formData.language}
                                onChange={handleInputChange('language')}
                                error={!!errors.language}
                                helperText={errors.language}
                                required
                            >
                                {languages.map((lang) => (
                                    <MenuItem key={lang._id} value={lang._id}>
                                        {lang.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LevelSelector
                                value={formData.level}
                                onChange={handleInputChange('level')}
                                error={errors.level}
                                required
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Summary"
                                value={formData.summary}
                                onChange={handleInputChange('summary')}
                                multiline
                                rows={3}
                                placeholder="Brief summary for display in post listings..."
                                helperText="This summary will be shown in post listings but not in the actual post content"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Category"
                                value={formData.category}
                                onChange={handleInputChange('category')}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Author"
                                value={formData.author}
                                onChange={handleInputChange('author')}
                                placeholder="Content author name"
                            />
                        </Grid>

                        {/* Source Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Source Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Source Title"
                                value={formData.sourceTitle}
                                onChange={handleInputChange('sourceTitle')}
                                placeholder="Original source title"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Source URL"
                                value={formData.sourceUrl}
                                onChange={handleInputChange('sourceUrl')}
                                error={!!errors.sourceUrl}
                                helperText={errors.sourceUrl}
                                placeholder="https://example.com"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Thumbnail URL"
                                    value={formData.thumbUrl}
                                    onChange={handleInputChange('thumbUrl')}
                                    error={!!errors.thumbUrl}
                                    helperText={errors.thumbUrl}
                                    placeholder="https://example.com/image.jpg"
                                />
                                
                                {/* Thumbnail Preview */}
                                {formData.thumbUrl && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Thumbnail Preview:
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: 2,
                                                p: 1,
                                                backgroundColor: '#f9f9f9'
                                            }}
                                        >
                                            <img
                                                src={formData.thumbUrl}
                                                alt="Thumbnail preview"
                                                style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '150px',
                                                    objectFit: 'cover',
                                                    borderRadius: '4px',
                                                    display: 'block'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                                onLoad={(e) => {
                                                    e.target.style.display = 'block';
                                                    e.target.nextSibling.style.display = 'none';
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    display: 'none',
                                                    width: '200px',
                                                    height: '150px',
                                                    backgroundColor: '#f5f5f5',
                                                    border: '2px dashed #ccc',
                                                    borderRadius: 1,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    Failed to load image
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                
                                {!formData.thumbUrl && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                width: '200px',
                                                height: '150px',
                                                backgroundColor: '#f5f5f5',
                                                border: '2px dashed #ccc',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                No Thumbnail
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Add a URL above to preview
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Tags */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Tags
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" gap={1} alignItems="center" mb={2}>
                                <TextField
                                    label="Add Tag"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyPress={handleTagInputKeyPress}
                                    size="small"
                                />
                                <Button 
                                    onClick={handleAddTag}
                                    variant="outlined"
                                    disabled={!tagInput.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                            
                            {errors.tags && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {errors.tags}
                                </Alert>
                            )}

                            <Box display="flex" gap={1} flexWrap="wrap">
                                {formData.tags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Grid>

                        {/* Publishing Status */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Publishing
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Publication Settings
                                </Typography>
                                
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Note:</strong> Translation checking is not available at the metadata level. 
                                        Please ensure all content chunks have been properly translated before marking as ready to serve.
                                    </Typography>
                                </Alert>

                                {/* Ready to Serve Toggle */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isReadyToServe}
                                            onChange={handleReadyToServeToggle}
                                            name="isReadyToServe"
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Ready to Serve
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formData.isReadyToServe 
                                                    ? "This post is ready for users to view" 
                                                    : "This post is not ready for users to view"
                                                }
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Ready to Serve Confirmation Dialog */}
            <Dialog
                open={readyToServeDialog.open}
                onClose={() => handleReadyToServeConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning color="warning" />
                        Confirm Ready to Serve
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Before marking this long post as "Ready to Serve", please ensure you have:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 2 }}>
                        <li>
                            <Typography variant="body2">
                                Created and organized all content chunks properly
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Translated all content chunks completely
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Verified post metadata (title, tags, thumbnail) is correct
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Reviewed the content flow and formatting for quality
                            </Typography>
                        </li>
                    </Box>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Have you completed these checks and is the post ready for users?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleReadyToServeConfirm(false)}>
                        No, I need to check more
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleReadyToServeConfirm(true)}
                    >
                        Yes, it is ready
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LongPostMetadataEditor; 